import { Chip, Grid, Stack } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";

import { memo } from "react";
import NoData from "../../../../components/UI/NoData";
import Table from "../../../../components/UI/Table/Table";
import useDate from "../../../../hooks/useDate";
import { IInsuranceFavorite } from "../model";
import DeleteInsurance from "./DeleteInsurance";
import EditInsurance from "./EditInsurance";

const translationPath = "page.schedules.insurance.";

interface IRefundsTableProps {
  insurance: IInsuranceFavorite[];
  onDelete: (id: string) => void;
  loading?: boolean;
}

const InsuranceTable = ({
  insurance,
  onDelete,
  loading,
}: IRefundsTableProps) => {
  const { t } = useTranslation();

  const { dayjs } = useDate();

  const columns: GridColDef<IInsuranceFavorite>[] = [
    {
      field: "insuranceName",
      headerName: `${t(translationPath + "insuranceName")}`,
      flex: 1,
      minWidth: 200,
    },
    {
      field: "individual",
      headerName: `${t(translationPath + "individual")}`,
      flex: 1,
      minWidth: 200,
      renderCell: ({ row: { individual } }) => individual.name,
    },
    {
      field: "cardNumber",
      headerName: `${t(translationPath + "cardNumber")}`,
      flex: 1,
    },
    {
      field: "cardValidity",
      headerName: `${t(translationPath + "cardValidity")}`,
      flex: 1,
      renderCell: ({ row: { cardValidity } }) =>
        dayjs(cardValidity).isValid()
          ? dayjs(cardValidity).add(1, "day").format("L")
          : "--",
    },
    {
      field: "main",
      headerName: "",
      width: 150,
      renderCell: ({ row: { main } }) =>
        main ? (
          <Chip
            size="small"
            color="primary"
            label={t(translationPath + "addInsurance.main")}
          />
        ) : (
          ""
        ),
    },
    {
      field: "actions",
      headerName: "",
      flex: 1,
      maxWidth: 100,
      renderCell: ({ row: { code } }) => {
        if (code === "1" || code === "2") {
          return null;
        }

        return (
          <Stack flexDirection={"row"} gap={1}>
            <DeleteInsurance id={code} onDelete={onDelete} />
            <EditInsurance id={code} />
          </Stack>
        );
      },
    },
  ];

  return (
    <Grid item container xs={12}>
      <Table
        columns={columns}
        loading={loading}
        rows={insurance}
        rowHeight={70}
        getRowId={(d) => d.code}
      />

      {insurance.length === 0 && (
        <Grid container mt={5}>
          <Grid item xs={12} sm={12}>
            <NoData />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default memo(InsuranceTable);
